import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { StringFormatter } from '@repo/toolkit/string'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { TravelTwoIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { getEnrollmentPickupActions } from '../../../api/enrollments/can-modify-enrollment-pickup'
import { getEnrollment } from '../../../api/enrollments/get-enrollment'
import { getEnrollmentPickup } from '../../../api/enrollments/get-enrollment-pickup'
import { openEnrollmentPickup } from '../../../api/enrollments/open-enrollment-pickup'
import { updateEnrollmentPickup } from '../../../api/enrollments/update-enrollment-pickup'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import { mapEnrollmentToEnrollmentPickupInput } from '../../../components/pages/language-test/client-space/client-space-enrollment.utils'
import { ClientEnrollmentPickup } from '../../../components/pages/language-test/client-space/enrollment-pickup'
import { EnrollmentPickupForm } from '../../../components/pages/language-test/client-space/enrollment-pickup.form'
import { ClientHeader } from '../../../components/pages/language-test/client-space/shared-client-space-ui'
import { Paths } from '../../../lib/constants/path'
import { enrollmentChildPickupFields } from '../../../lib/types/fields/enrollments/enrollment'
import { enrollmentPickupBaseFields } from '../../../lib/types/fields/enrollments/enrollment-pickup'
import { EnrollmentProperties } from '../../../lib/types/models/enrollment/enrollment.properties'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'

export default function ClientPickupPage() {
  const params = useParams<{ id: string }>()
  const id = Number(params.id)
  const { navigate } = useNavigator()

  const { data: enrollment, refetch } = useQuery({
    queryKey: ['enrollment', id, enrollmentChildPickupFields],
    queryFn: () => getEnrollment(id, enrollmentChildPickupFields),
    enabled: !!id
  })

  const {
    data: enrollmentPickup,
    isPending,
    refetch: refetchEnrollmentPickup
  } = useQuery({
    queryKey: ['client-enrollment-pickup', id],
    queryFn: () => getEnrollmentPickup(id, enrollmentPickupBaseFields),
    enabled: !!id
  })

  const { data: getEnrollmentPickupActionsData, refetch: refetchGetEnrollmentPickupActions } =
    useQuery({
      queryKey: ['get-enrollment-pickup-actions', id],
      queryFn: () => getEnrollmentPickupActions(id),
      enabled: !!id
    })

  const { mutateAsync } = useMutation({
    mutationFn: updateEnrollmentPickup,
    onError: showAxiosError,
    onSuccess: () => {
      refetch()
      refetchGetEnrollmentPickupActions()
      refetchEnrollmentPickup()
      toast.success("Informations de trajet individuel de l'enfant mis à jour")
    }
  })

  const { mutateAsync: openEnrollmentPickupMutation, isPending: isOpeningEnrollmentPickup } =
    useMutation({
      mutationFn: openEnrollmentPickup,
      onError: showAxiosError,
      onSuccess: () => {
        refetch()
        refetchGetEnrollmentPickupActions()
        refetchEnrollmentPickup()
        toast.success("Informations de trajet individuel de l'enfant ouvertes")
      }
    })

  const isSubmitted = enrollment?.childPickupSubmitted

  const returnDate = EnrollmentProperties.isOtoIndividual(enrollment)
    ? enrollment?.otoIndividualProposition?.returnDate
    : enrollment?.mainProposition?.journey?.date?.returnDate

  if (!enrollment || isPending) return <LoadingScreen />

  return (
    <div className={'flex flex-col gap-4'}>
      <ClientHeader
        title={'Qui vient chercher votre enfant à la fin du séjour ?'}
        icon={TravelTwoIcon}
      />

      {isSubmitted && (
        <Badge
          variant={'success'}
          text="Merci, nous avons bien reçu les informations de trajet individuel de votre enfant"
        />
      )}

      <InnerBox
        className={'bg-white-hover rounded-lg'}
        title={`Informations concernant la personne qui viendra chercher ${enrollment.childFirstName ? StringFormatter.capitalize(enrollment.childFirstName) : '-'} le dernier jour du séjour`}
      >
        <Typography variant={'secondary-body'}>
          Vous avez choisi de ne pas réserver tout ou partie du voyage accompagné par LEC. Nous
          avons donc besoin de connaître l'identité de la personne qui viendra chercher{' '}
          {enrollment.childFirstName ? StringFormatter.capitalize(enrollment.childFirstName) : '-'}{' '}
          directement sur le lieu de son séjour le{' '}
          {valueRender(returnDate, (d) => DateFormatter.DDMMYYYY(d))}. Merci de noter que cette
          personne devra impérativement se présenter durant la créneau horaire indiqué dans
          l'onglet{' '}
          <Typography.Link
            variant={'secondary-body'}
            className={'inline-block'}
            color={tailwindColors.primary.DEFAULT}
            onClick={() => navigate(Paths.CLIENT_PERSONAL_SPACE_CANCEL_JOURNEY)}
          >
            Mon Carnet de Voyage
          </Typography.Link>
          . Elle devra être munie d'une pièce d'identité avec photo afin que le responsable puisse
          autoriser le départ du jeune. Vous pourrez modifier les informations saisies{' '}
          <span className={'font-bold'}>jusqu'à 72 heures avant le dernier jour du séjour.</span>
        </Typography>
      </InnerBox>

      {enrollmentPickup && (!getEnrollmentPickupActionsData?.canView || isSubmitted) ? (
        <>
          <ClientEnrollmentPickup data={enrollmentPickup} />

          {getEnrollmentPickupActionsData?.canModify && (
            <Button
              label={'Modifier'}
              className={'w-fit self-end mobile:w-full'}
              disabled={isOpeningEnrollmentPickup}
              onClick={() => openEnrollmentPickupMutation(enrollment.id)}
            />
          )}
        </>
      ) : (
        <EnrollmentPickupForm
          enrollmentId={enrollment.id}
          defaultValues={
            enrollmentPickup && mapEnrollmentToEnrollmentPickupInput(enrollmentPickup)
          }
          buttonsConfig={[
            {
              type: 'submit',
              label: 'Confirmer',
              variant: 'primary',
              className: 'mobile:w-full',
              action: (data) =>
                mutateAsync({ ...data, id: enrollment.id, childPickupSubmitted: true })
            }
          ]}
        />
      )}
    </div>
  )
}
